import { z } from 'zod'

export const DEFAULT_PAGE_SIZE = 10
export const DEFAULT_INFINITE_PAGE_SIZE = 15
export const DEFAULT_INFINITE_MAX_PAGES = 10

export const paginationSchema = z.object({
  pageIndex: z.number().min(0),
  pageSize: z.number().min(1),
  totalCount: z.number().min(0),
})

export const paginationRequestSchema = z.object({
  pageIndex: z.coerce.number().min(0).default(0).optional(),
  pageSize: z.coerce.number().min(1).default(DEFAULT_PAGE_SIZE).optional(),
})
export type PaginationRequest = z.infer<typeof paginationRequestSchema>

const epochSecondsCompatWarned = new Set<string>()

/**
 * TEMPORARY: the deployed API still sends epoch seconds. Coerces them to ISO
 * at the wire boundary so the app only ever sees ISO strings. Delete this arm
 * (grep epochSecondsCompat) once the API sends ISO everywhere.
 */
const epochSecondsCompatSchema = z
  .number()
  .finite()
  .transform((seconds, ctx) => {
    if (import.meta.env.DEV) {
      const field = ctx.path.join('.') || '(root)'
      if (!epochSecondsCompatWarned.has(field)) {
        epochSecondsCompatWarned.add(field)
        console.warn(
          `epochSecondsCompat: "${field}" still arrives as epoch seconds`
        )
      }
    }
    const ms = seconds * 1000
    // An out-of-range Date would make toISOString() throw below.
    if (Number.isNaN(new Date(ms).getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Epoch-seconds value is out of range for a valid Date',
      })
      return z.NEVER
    }
    return new Date(ms).toISOString()
  })

/**
 * ISO8601 wire format for absolute timestamps (.NET DateTimeOffset).
 * Accepts `Z`, numeric offsets (+00:00), and fractional seconds.
 */
export const isoDateTimeSchema = z.union([
  z.string().datetime({ offset: true }),
  epochSecondsCompatSchema,
])

export const timestampsSchema = z.object({
  createdAt: isoDateTimeSchema,
  lastUpdatedAt: isoDateTimeSchema,
})

export const sessionTimestampsSchema = z.object({
  startedAt: isoDateTimeSchema.nullable(),
  stoppedAt: isoDateTimeSchema.nullable(),
})

/**
 * Request-side timestamps stay strict ISO regardless of what the read side
 * tolerates — requests are outbound, we control the format.
 */
export const strictIsoDateTimeSchema = z.string().datetime({ offset: true })

const utcIsoDateTimeSchema = strictIsoDateTimeSchema.transform((value) =>
  new Date(value).toISOString()
)

export const durationRangeRequestSchema = z.object({
  from: utcIsoDateTimeSchema,
  to: utcIsoDateTimeSchema,
})
export type DurationRangeRequest = z.infer<typeof durationRangeRequestSchema>

export const ORDER_DIRECTIONS = {
  ascending: 'ascending',
  descending: 'descending',
} as const

export const DEFAULT_ORDER = {
  field: 'createdAt',
  direction: ORDER_DIRECTIONS.descending,
}

const baseOrderBySchema = z.object({
  field: z.string(),
  direction: z.nativeEnum(ORDER_DIRECTIONS),
})
export type OrderBy = z.infer<typeof baseOrderBySchema>

export const listQuerySchema = z
  .object({
    orderBy: z.array(baseOrderBySchema).optional(),
  })
  .merge(paginationRequestSchema)
export type ListQuery = z.infer<typeof listQuerySchema>

// Shared text-search filters expected across multiple entity list endpoints.
// All fields are optional and intended to be case-insensitive on the API side.
export const sharedFilterBySearchSchema = z.object({
  metadataKeySearch: z.string().min(1).optional(),
  metadataValueSearch: z.string().min(1).optional(),
  search: z.string().min(1).optional(),
})
export type SharedFilterBySearch = z.infer<typeof sharedFilterBySearchSchema>

export function createListQuerySchema<T extends string>(
  fields: T[],
  defaults?: {
    pageIndex?: number
    pageSize?: number
    orderBy?: Array<{ field: T; direction: keyof typeof ORDER_DIRECTIONS }>
  }
) {
  const orderSchema = z.object({
    field: z.enum(fields as [T, ...T[]]),
    direction: z.nativeEnum(ORDER_DIRECTIONS),
  })

  return z.object({
    pageIndex: z.coerce
      .number()
      .min(0)
      .default(defaults?.pageIndex ?? 0),
    pageSize: z.coerce
      .number()
      .min(1)
      .default(defaults?.pageSize ?? DEFAULT_PAGE_SIZE),
    orderBy: z.array(orderSchema).default(defaults?.orderBy ?? []),
  })
}

export const baseFilterBySchema = z.object({
  search: z.string().optional(),
  metadataKeySearch: z.string().optional(),
  metadataValueSearch: z.string().optional(),
})
export type BaseFilterBy = z.infer<typeof baseFilterBySchema>

export function createFilterableListQuerySchema<
  T extends string,
  F extends z.ZodTypeAny,
>(
  fields: T[],
  filterBySchema: F,
  defaults?: {
    pageIndex?: number
    pageSize?: number
    orderBy?: Array<{ field: T; direction: keyof typeof ORDER_DIRECTIONS }>
  }
) {
  return createListQuerySchema(fields, defaults).extend({
    filterBy: filterBySchema.optional(),
  })
}
